import React from 'react';
import { observer } from 'mobx-react';
import { Alert, Input, Table } from 'reactstrap';
import { PrePaymentFrequency, PrePaymentFrequencyOption, Server } from '~/lib/ApiModel';
import { getAppContext } from '~/lib/AppContext';
import BusyButton from '~/components/BusyButton';
import WaitFor from '~/components/WaitFor';
import { formatDate } from '~/lib/helper/StringHelper';
import UnmountHelper from '~/lib/helper/UnmountHelper';
import FormStore from '~/lib/store/FormStore';
import { PaymentFrequencyStore } from '~/lib/store/PaymentFrequencyStore';

function nextFrequency(server: Server): PrePaymentFrequency {
    return server.pre_payment?.next_pre_payment_frequency ?? 'post-paid';
}

type PaymentFrequencyRowProps = {
    server: Server;
    options: PrePaymentFrequencyOption[];
    onServerUpdated: (server: Server) => void;
};

@observer
class PaymentFrequencyRow extends React.Component<PaymentFrequencyRowProps> {
    // this.props is assigned by React during super(props), before field initializers run,
    // so the server's actual frequency is the initial value with no transient 'post-paid' render.
    form = new FormStore<{ pre_payment_frequency: PrePaymentFrequency }>({
        pre_payment_frequency: nextFrequency(this.props.server),
    });

    unmountHelper = new UnmountHelper(this);

    // Rows render inside the options WaitFor, so the raw-frequency fallback is effectively unreachable.
    descriptionFor(frequency: string): string {
        return this.props.options.find((o) => o.pre_payment_frequency === frequency)?.description.trim() ?? frequency;
    }

    async doChangeFrequency() {
        const server = this.props.server;
        const frequency = this.form.values.pre_payment_frequency;

        const confirmed = await getAppContext().confirm({
            title: 'Confirm Payment Frequency Change',
            message: (
                <>
                    Change the payment frequency for <strong>{server.name}</strong> to "{this.descriptionFor(frequency)}
                    "? The change will take effect from the start of the next billing period.
                </>
            ),
            confirmText: 'Change Payment Frequency',
        });
        if (!confirmed) return;

        // The row has no inline field-error surface, so route every error (including field-level
        // validation) to the global modal — otherwise formErrorModalBody would defer field errors
        // to an inline surface that doesn't exist here and they'd never be shown.
        await this.form.doSubmit(
            async () => {
                const updated = await this.unmountHelper.cancelIfUnmounted(
                    getAppContext().apiClient.changeServerPaymentFrequency(server.id, {
                        pre_payment_frequency: frequency,
                    })
                );
                this.props.onServerUpdated(updated);
                getAppContext().notify.fire({
                    title: 'Payment Frequency Changed',
                    body: (
                        <p>
                            Payment frequency for {server.name} is now "{this.descriptionFor(frequency)}".
                        </p>
                    ),
                    color: 'success',
                });
            },
            { fieldErrorsToModal: true, errorSummary: 'Unable to change payment frequency' }
        );
    }

    render() {
        const { server } = this.props;
        const form = this.form;
        const current = server.pre_payment?.pre_payment_frequency ?? 'post-paid';
        const next = nextFrequency(server);
        return (
            <tr>
                <td className="collapsed-header">{server.name}</td>
                <td className={server.pre_payment?.paid_until ? undefined : 'collapsed-hidden'}>
                    <span className="th">Next Invoice Due</span>
                    <div>{server.pre_payment?.paid_until ? formatDate(server.pre_payment.paid_until) : '-'}</div>
                </td>
                <td>
                    <span className="th">Payment Frequency</span>
                    <div>
                        <Input
                            type="select"
                            aria-label={`Payment frequency for ${server.name}`}
                            value={form.values.pre_payment_frequency}
                            onChange={(e) =>
                                form.setValue(
                                    (v) => (v.pre_payment_frequency = e.currentTarget.value as PrePaymentFrequency)
                                )
                            }
                        >
                            {this.props.options.map((o) => (
                                <option key={o.pre_payment_frequency} value={o.pre_payment_frequency}>
                                    {o.description}
                                </option>
                            ))}
                        </Input>
                        {next !== current && (
                            <small className="text-muted">
                                Invoices are currently "{this.descriptionFor(current)}"; the new frequency applies from
                                the next billing period.
                            </small>
                        )}
                    </div>
                </td>
                <td>
                    <BusyButton
                        busy={form.isSubmitting}
                        color="primary"
                        disabled={form.values.pre_payment_frequency === next}
                        onClick={() => void this.doChangeFrequency()}
                    >
                        Save
                    </BusyButton>
                </td>
            </tr>
        );
    }
}

type PaymentFrequencyTableProps = {
    servers: Server[];
    onServerUpdated: (server: Server) => void;
};

export default class PaymentFrequencyTable extends React.Component<PaymentFrequencyTableProps> {
    options = new PaymentFrequencyStore(getAppContext().apiClient);

    componentDidMount() {
        void this.options.load();
    }

    render() {
        return (
            <div className="paymentFrequencyTable">
                <p>
                    To change the payment frequency for a server use the form below. The change will take effect from
                    the start of the next billing period.
                </p>
                <WaitFor
                    operation={this.options}
                    loadingText="Loading payment frequency options..."
                    errorText="Error loading payment frequency options. Refresh the page to try again."
                >
                    {() =>
                        this.props.servers.length === 0 ? (
                            <Alert color="info">There are no servers to display.</Alert>
                        ) : (
                            <Table responsive className="table-white table-collapse">
                                <thead>
                                    <tr>
                                        <th>Hostname</th>
                                        <th>Next Invoice Due</th>
                                        <th>Payment Frequency</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.props.servers.map((s) => (
                                        <PaymentFrequencyRow
                                            key={s.id}
                                            server={s}
                                            options={this.options.items}
                                            onServerUpdated={this.props.onServerUpdated}
                                        />
                                    ))}
                                </tbody>
                            </Table>
                        )
                    }
                </WaitFor>
            </div>
        );
    }
}
